import React, { Children, forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import Infobar from '../Infobar/Infobar'
import {
  Box,
  ChartIcon,
  GridContainer,
  GridItem,
  NoData,
  NoDataImage,
  NoDataText,
  NoDataTitle,
  Root,
  TableIcon,
  ViewButton
} from './DataLayout.styles'

export { default as DataLayoutChild } from './DataLayout.child'
export { default as useDataLayout } from './DataLayout.hook'

export const DataLayoutViews = {
  CHART: 0,
  TABLE: 1
}
export const DataLayoutViewButton = ViewButton

/**
 * The DataLayout's component.
 */
const DataLayout = forwardRef((props, ref) => {
  const {
    children,
    custom = {
      controls: (chartChild, tableChart) => (
        <>
          {chartChild}
          {tableChart}
        </>
      ),
      views: []
    },
    disableCaptureImage,
    disableChart,
    disableDownloadFile,
    disableFooter,
    disableHeader,
    disableTable,
    header,
    hidden,
    headerProps,
    infographic,
    initialDisplayedView,
    initialExpandedInfographic,
    marginFooter,
    noData,
    noDataText,
    paddingFooter,
    updatedDate,
    onCaptureImage,
    onChangeView,
    onDownloadFile
  } = props
  const [displayedView, setDisplayedView] = useState(initialDisplayedView)
  const [expandedInfographic, setExpandedInfographic] = useState(initialExpandedInfographic)
  const handleChangeDisplayedView = updatedView => () => {
    setDisplayedView(updatedView)
    onChangeView(updatedView)
  }
  const views = useMemo(() => {
    const childrenCollection = Children.toArray(children)

    return childrenCollection
      .filter(
        child =>
          child?.props?.name === 'DataLayoutChild' &&
          [...Object.values(DataLayoutViews), ...custom.views].indexOf(child?.props?.view) !== -1
      )
      .map(child => ({ child, rule: view => view === child?.props?.view && !noData }))
  }, [children, custom.views, noData])
  const handleChangeExpandedInforgraphic = useCallback(() => setExpandedInfographic(currentValue => !currentValue), [])

  useImperativeHandle(
    ref,
    () => ({
      infographic: expandedInfographic,
      view: displayedView,
      setInfographic: setExpandedInfographic,
      setView: updatedView => {
        setDisplayedView(updatedView)
        onChangeView(updatedView)
      }
    }),
    [initialDisplayedView, displayedView, expandedInfographic]
  )
  const mdValue = useMemo(() => {
    if (views.length === 1 || hidden) return 12
    return 9.9
  }, [views.length, headerProps?.leftItem])

  return (
    <Root>
      {!disableHeader && (
        <GridContainer alignItems="center" justifyContent="space-between" {...headerProps}>
          <GridItem xs={12} md={mdValue}>
            {header}
          </GridItem>
          {views.length > 1 && (
            <GridItem xs={12} md={2} spacing={2}>
              <Box display="flex" justifyContent="flex-end">
                {custom.controls(
                  <ViewButton
                    hidden={hidden}
                    disabled={disableChart || noData}
                    selected={displayedView === DataLayoutViews.CHART}
                    onClick={handleChangeDisplayedView(DataLayoutViews.CHART)}
                  >
                    <ChartIcon />
                    Gráfico
                  </ViewButton>,
                  <ViewButton
                    hidden={hidden}
                    disabled={disableTable || noData}
                    selected={displayedView === DataLayoutViews.TABLE}
                    onClick={handleChangeDisplayedView(DataLayoutViews.TABLE)}
                  >
                    <TableIcon /> Datos
                  </ViewButton>
                )}
              </Box>
            </GridItem>
          )}
        </GridContainer>
      )}
      <GridContainer>
        <GridItem xs={12}>
          {views.map(view => view.rule(displayedView) && view.child)}
          {noData && (
            <NoData>
              <NoDataImage alt="Sin información" src="/assets/images/logo-dccp.svg" />
              <NoDataTitle>No existe información</NoDataTitle>
              {noDataText && <NoDataText>{noDataText}</NoDataText>}
            </NoData>
          )}
        </GridItem>
      </GridContainer>
      {!disableFooter && (
        <>
          <Infobar
            disableDownloadChart={disableCaptureImage || noData}
            disableDownloadCsv={disableDownloadFile || noData}
            disableInfographic={!infographic || noData || displayedView !== DataLayoutViews.CHART}
            expandArrow={expandedInfographic}
            infographic={infographic}
            latestUpdate={updatedDate}
            marginFooter={marginFooter}
            onCaptureImage={onCaptureImage}
            onDownloadFile={onDownloadFile}
            paddingFooter={paddingFooter}
            setExpandArrow={handleChangeExpandedInforgraphic}
          />
        </>
      )}
    </Root>
  )
})

DataLayout.defaultProps = {
  custom: undefined,
  disableCaptureImage: false,
  disableChart: false,
  disableDownloadFile: false,
  disableFooter: false,
  disableHeader: false,
  disableTable: false,
  header: null,
  hidden: false,
  headerProps: {
    leftItem: 11,
    rightItem: 1
  },
  infographic: null,
  initialDisplayedView: DataLayoutViews.CHART,
  initialExpandedInfographic: false,
  marginFooter: '5px 0 0',
  noData: false,
  noDataText: '',
  paddingFooter: '24px',
  updatedDate: null,
  controls: undefined,
  onCaptureImage: () => undefined,
  onChangeView: () => undefined,
  onDownloadFile: () => undefined
}

DataLayout.propTypes = {
  children: PropTypes.node.isRequired,
  custom: PropTypes.shape({
    controls: PropTypes.func,
    views: PropTypes.arrayOf(PropTypes.number)
  }),
  disableCaptureImage: PropTypes.bool,
  disableChart: PropTypes.bool,
  disableDownloadFile: PropTypes.bool,
  disableFooter: PropTypes.bool,
  disableHeader: PropTypes.bool,
  disableTable: PropTypes.bool,
  header: PropTypes.node,
  hidden: PropTypes.bool,
  headerProps: PropTypes.shape({
    ...GridContainer.propTypes,
    leftItem: PropTypes.number,
    rightItem: PropTypes.number
  }),
  infographic: PropTypes.node,
  initialDisplayedView: PropTypes.oneOf([DataLayoutViews.CHART, DataLayoutViews.TABLE]),
  initialExpandedInfographic: PropTypes.bool,
  marginFooter: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  noData: PropTypes.bool,
  noDataText: PropTypes.string,
  updatedDate: PropTypes.string,
  paddingFooter: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  controls: PropTypes.func,
  onCaptureImage: PropTypes.func,
  onChangeView: PropTypes.func,
  onDownloadFile: PropTypes.func
}

export default DataLayout
